import { useCallback, useEffect, useRef, useState } from "react";
import { FormHelperText } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import FormSection from "@components/FormSection/FormSection";
import { useNotification } from "@context/NotificationContext";
import { useAzureBlobUpload } from "@features/port-security/hooks/useAzureBlobUpload";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { t } from "i18next";
import { FormProvider } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import type { TruckDeliveryNoteFormData, TruckGatepassOutletContext } from "./types";

const TRANSLATION_KEY = "gatepass";
const FIELD_NAME = "deliveryNotesDocuments" as const;
const DELIVERY_NOTES_CERTIFICATE_TYPE = "DELIVERY_NOTES";
const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
const MAX_DELIVERY_NOTE_FILES = 5;

const DeliveryNotesDocuments = () => {
  const { formData, updateFormData } = useOutletContext<TruckGatepassOutletContext>();
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const { notify } = useNotification();
  const [uploadError, setUploadError] = useState<string | null>(null);

  const files = useUploadStore((state) => state.filesByKey[FIELD_NAME] ?? EMPTY_UPLOAD_ENTRIES);
  const setFilesForKey = useUploadStore((state) => state.setFilesForKey);
  const hasHydratedFromFormRef = useRef(false);
  const formError = formData.formState.errors.deliveryNotesDocuments;
  const formErrorMessage = typeof formError?.message === "string" ? formError.message : null;

  const setFiles = useCallback(
    (next: UploadEntry[] | ((prev: UploadEntry[]) => UploadEntry[])) => {
      setFilesForKey(FIELD_NAME, next, { maxFiles: MAX_DELIVERY_NOTE_FILES });
    },
    [setFilesForKey],
  );

  useEffect(() => {
    if (hasHydratedFromFormRef.current) return;
    hasHydratedFromFormRef.current = true;

    const current = formData.getValues(FIELD_NAME);
    if (Array.isArray(current) && current.length > 0) {
      setFilesForKey(FIELD_NAME, current as UploadEntry[], { maxFiles: MAX_DELIVERY_NOTE_FILES });
    }
  }, [formData, setFilesForKey]);

  const applyFormValueFromFiles = useCallback(
    (entries: UploadEntry[]) => {
      const mapped = entries.map((entry, idx) => {
        if (entry instanceof File) {
          return {
            name: entry.name,
            url: undefined,
            certificateType: DELIVERY_NOTES_CERTIFICATE_TYPE,
            idx,
          };
        }
        const typedEntry = entry as Record<string, unknown>;
        const normalizedUrl = String(
          typedEntry.url ?? typedEntry.documentUrl ?? typedEntry.blobUrl ?? "",
        ).trim();
        return {
          name: String(typedEntry.name ?? ""),
          url: normalizedUrl || undefined,
          certificateType:
            String(
              typedEntry.certificateType ?? typedEntry.type ?? DELIVERY_NOTES_CERTIFICATE_TYPE,
            ) || DELIVERY_NOTES_CERTIFICATE_TYPE,
          idx: typeof typedEntry.idx === "number" ? typedEntry.idx : idx,
        };
      });

      const current = formData.getValues(FIELD_NAME) ?? [];
      const isSame =
        current.length === mapped.length &&
        mapped.every((item, idx) => {
          const existing = current[idx] ?? {};
          const existingUrl = String(
            existing.url ?? existing.documentUrl ?? existing.blobUrl ?? "",
          );
          return (
            String(existing.name ?? "") === String(item.name ?? "") &&
            existingUrl === String(item.url ?? "") &&
            String(existing.certificateType ?? existing.type ?? "") ===
              String(item.certificateType ?? "") &&
            Number(existing.idx ?? idx) === Number(item.idx ?? idx)
          );
        });

      if (isSame) return;

      formData.setValue(FIELD_NAME, mapped as TruckDeliveryNoteFormData[], {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      updateFormData(FIELD_NAME, mapped);
    },
    [formData, updateFormData],
  );

  useEffect(() => {
    applyFormValueFromFiles(files);
  }, [files]);

  const handleUploadError = useCallback(
    (error: Error) => {
      notify({
        title: t("gatepass.notifications.gatepass-request-upload-error-title"),
        description: t("gatepass.notifications.gatepass-request-upload-error-description"),
        type: "error",
      });
      console.error("Error uploading delivery notes document", error);
    },
    [notify],
  );

  const handleUploadSuccess = useCallback(
    (dataArray: { documentUrl: string; certificateType: string }[], context: { idx?: number }) => {
      if (!Array.isArray(dataArray) || dataArray.length === 0) return;

      const current = useUploadStore.getState().getFilesForKey(FIELD_NAME);
      const idxFromContext = typeof context.idx === "number" ? context.idx : 0;
      const uploadedUrl = dataArray[0]?.documentUrl;
      if (!uploadedUrl) return;

      const next = [...current];
      const currentEntry = next[idxFromContext];
      const currentName =
        currentEntry instanceof File ? currentEntry.name : String(currentEntry?.name ?? "");

      next[idxFromContext] = {
        name: currentName,
        url: uploadedUrl,
        certificateType: DELIVERY_NOTES_CERTIFICATE_TYPE,
        idx: idxFromContext,
      };

      useUploadStore
        .getState()
        .setFilesForKey(FIELD_NAME, next, { maxFiles: MAX_DELIVERY_NOTE_FILES });
      applyFormValueFromFiles(next);
    },
    [applyFormValueFromFiles],
  );

  const uploadDocumentsToAzure = useAzureBlobUpload({
    handleUploadSuccess: (dataArray, context) =>
      handleUploadSuccess(dataArray, { idx: context.idx }),
    handleUploadError,
    transactionContext: {
      module: "gatepass",
      screen: "TruckGatepassDeliveryNotes",
      action: "upload_gatepass_document",
      entityType: "gatepass_document",
    },
  });

  const handleFieldChangeForAzure = useCallback(
    async (uploadedFiles: File[]) => {
      setUploadError(null);
      const currentEntries = useUploadStore.getState().getFilesForKey(FIELD_NAME);
      applyFormValueFromFiles(currentEntries);

      if (!Array.isArray(uploadedFiles) || uploadedFiles.length === 0) return;

      // Map each uploaded file to its absolute index in the store list so upload
      // callbacks update the correct entry when files are added in multiple batches
      const usedIndices = new Set<number>();
      const getAbsoluteIndexForFile = (file: File) => {
        for (let i = 0; i < currentEntries.length; i += 1) {
          if (usedIndices.has(i)) continue;
          const entry = currentEntries[i];
          if (entry === file) {
            usedIndices.add(i);
            return i;
          }
        }
        return -1;
      };

      await Promise.all(
        uploadedFiles.map(async (file, fallbackIdx) => {
          try {
            const sasTokenResponse = await getSasToken({
              prefix: SasTokenPrefix.GATEPASS_SECURITY_CLEARANCE,
              contentType: file.type || null,
              privilege: SasTokenPrivilege.WRITE,
              blobUrl: null,
            });

            const absoluteIdx = getAbsoluteIndexForFile(file);
            await uploadDocumentsToAzure.mutateAsync({
              file,
              blobUrl: sasTokenResponse.blobUrl,
              sasToken: sasTokenResponse.sasToken,
              certificateType: DELIVERY_NOTES_CERTIFICATE_TYPE,
              storeKey: FIELD_NAME,
              idx: absoluteIdx >= 0 ? absoluteIdx : fallbackIdx,
            });
          } catch (error) {
            const normalizedError =
              error instanceof Error
                ? error
                : new Error("Failed to upload delivery notes document");
            handleUploadError(normalizedError);
          }
        }),
      );
    },
    [applyFormValueFromFiles, handleUploadError, uploadDocumentsToAzure],
  );

  return (
    <FormProvider {...formData}>
      <FormSection
        title={t(`${TRANSLATION_KEY}.delivery-notes-documents`, {
          defaultValue: "Delivery Notes Documents",
        })}
      >
        <div className="col-span-full">
          <DocumentUpload
            testId="truck-delivery-notes-documents"
            name={FIELD_NAME}
            conditions={FILE_UPLOAD_PROFILES.files}
            maxFiles={MAX_DELIVERY_NOTE_FILES}
            allowedExtensions={FILE_UPLOAD_PROFILES.files.format}
            validateDrop={(selectedCount) => {
              if (selectedCount > MAX_DELIVERY_NOTE_FILES) {
                return t("form-validation.max-files", {
                  max: MAX_DELIVERY_NOTE_FILES,
                });
              }

              return null;
            }}
            onValidationResult={setUploadError}
            uploadDescription={t("upload.dragOrDropDescription")}
            files={files}
            setFiles={setFiles}
            onUpload={handleFieldChangeForAzure}
          />
          {uploadError || formErrorMessage ? (
            <FormHelperText
              sx={{ ml: 0, fontWeight: 500, fontSize: 12, marginY: "4px", color: "error.main" }}
            >
              {uploadError || formErrorMessage}
            </FormHelperText>
          ) : null}
        </div>
        {files.length > 0 &&
          files.map((file, idx) => (
            <div
              className="col-span-full"
              key={`${file.name}-${(file as File).lastModified ?? idx}`}
            >
              <SingleFile
                testId={`truck-delivery-notes-documents-${idx}`}
                file={file}
                currentFileIndex={idx}
                fieldName={FIELD_NAME}
                setFiles={() => setFiles((prev) => prev)}
                isLoading={file instanceof File}
              />
            </div>
          ))}
      </FormSection>
    </FormProvider>
  );
};

export default DeliveryNotesDocuments;
